import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { useRef } from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import '../../../utils/index_orig/index.esm.js';
import '../../../utils/theme/theme-provider/index.esm.js';
import '../../../sheet/bottom-sheet/index.esm.js';
import '../../../sheet/popover-sheet/index.esm.js';
import '../../../sheet/side-sheet/index.esm.js';
import '../../../sheet/centered-sheet/index.esm.js';
import '../../../sheet/full-sheet/index.esm.js';
import { EGDSSheet } from '../../../sheet/sheet/index.esm.js';
import { EGDSSheetContent } from '../../../sheet/sheet-content/index.esm.js';
import '../../../sheet/sheet-close-button/index.esm.js';
import { EGDSSheetTransition } from '../../../sheet/sheet-transition/index.esm.js';
import { EGDSSheetFooter } from '../../../sheet/sheet-footer/index.esm.js';
import '../../../sheet/types/index.esm.js';
import '../../../spacing/types/index.esm.js';
import { EGDSSpacing } from '../../../spacing/spacing/index.esm.js';
import '../../../spacing/spacing-hr/index.esm.js';
import { EGDSButtonSize } from '../../../button/button/index.esm.js';
import { EGDSPrimaryButton } from '../../../button/primary-button/index.esm.js';
import '../../../button/paging-button/index.esm.js';
import '../../../toolbar/toolbar/index.esm.js';
import { EGDSToolbar } from '../../../toolbar/egds-toolbar/index.esm.js';
import '../../../toolbar/toolbar-container/index.esm.js';
import '../../../toolbar/transparent-toolbar/index.esm.js';
import { EGDSToolbarButton } from '../../../toolbar/toolbar-button/index.esm.js';
import { useDialog } from '@shared-ui/dialog-context';
import { EGDSAlert } from '../../alert/index.esm.js';
import styles from '../../../styles/uitk-egds-date-selector.css';
import { classNames } from '../../../utils/classnames/index.esm.js';
import { useIdCompatibility } from '../../../utils/use-id-compatibility/index.esm.js';
const getClassNameList = classNames.bind(styles);
const DialogCalendar = props => {
  const {
    alertTemplateComponent,
    alertTemplateData,
    children,
    calendarWrapper,
    skipToButtonRef,
    ctaItems,
    dateSelectorPlayback,
    dialogId,
    hideDialogSubmit,
    hideFooter,
    isOpen,
    sheetType,
    showFullSizePlaybackDateDisplay = true,
    showPlayback,
    triggerRef,
    isNestDialog
  } = props;
  const {
    closeCta,
    submitCta,
    clearDatesCta
  } = ctaItems;
  const l10n = useLocalization();
  const dialogQueryKey = isNestDialog ? 'pwaDialogNested' : undefined;
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog(`uitk-date-selector-dialog-${dialogId}`, dialogQueryKey);
  const sheetBaseClassName = sheetType === 'centered' ? 'uitk-date-selector-centered-sheet' : 'uitk-date-selector-fullsheet';
  const sheetClassNames = getClassNameList(sheetBaseClassName, {
    'uitk-date-selector-dialog-sheet-full-playback-display': showFullSizePlaybackDateDisplay
  });
  const wrapperContainerClass = 'uitk-date-selector-dialog-external-wrapper-container';
  const externalWrapperSheetClass = sheetType === 'centered' ? `${wrapperContainerClass}-centered-sheet` : `${wrapperContainerClass}-fullsheet`;
  const dateSelectorCalendarClassName = `uitk-date-selector-dialog`;
  const CalendarWrapper = React.useMemo(() => calendarWrapper ? calendarWrapper.component : React.Fragment, [calendarWrapper]);
  const buttonUID = useIdCompatibility();
  const prevIsDialogOpenRef = useRef(isDialogOpen);
  React.useEffect(() => {
    if (isOpen && !isDialogOpen) {
      // If the dialog has been closed via the escape key or by clicking the backdrop we need to update the interal isOpen state via the closeCta handler
      if (prevIsDialogOpenRef.current === isDialogOpen) {
        dialogActions.openDialog();
      } else {
        closeCta.handler?.();
      }
    } else if (!isOpen && isDialogOpen) {
      // Prevent a history.goBack() call by passing additionalQueryParams
      dialogActions.closeDialog({
        additionalQueryParams: {}
      });
    }
    prevIsDialogOpenRef.current = isDialogOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDialogOpen]); // Do not add dialogActions or this will always update
  return jsx("div", {
    className: getClassNameList(dateSelectorCalendarClassName),
    children: jsx(EGDSSheetTransition, {
      isVisible: isOpen,
      children: jsx(DialogComponent, {
        children: jsxs(EGDSSheet, {
          centeredSheetSize: sheetType === 'centered' ? 'small' : undefined,
          className: sheetClassNames,
          disableArrowKeyHandler: true,
          isVisible: isDialogOpen,
          returnFocusOnClose: false,
          triggerRef: triggerRef,
          type: sheetType,
          children: [jsx(EGDSToolbar, {
            childClassName: getClassNameList('uitk-calendar-limit-inline-size'),
            navigationContent: {
              navIconLabel: l10n.formatText('dateSelector.cancelButton.a11yLabel'),
              navType: 'close',
              // @ts-ignore
              onClick: closeCta.handler
            },
            toolbarActionContent: clearDatesCta?.handler ? jsx(EGDSToolbarButton, {
              buttonId: buttonUID,
              onClick: clearDatesCta.handler,
              disabled: clearDatesCta.disabled,
              children: clearDatesCta.label
            }) : null,
            children: !calendarWrapper && showPlayback && dateSelectorPlayback
          }), calendarWrapper ? jsxs(CalendarWrapper, {
            ...calendarWrapper?.props,
            className: getClassNameList(`${wrapperContainerClass} ${externalWrapperSheetClass}`),
            children: [calendarWrapper && showPlayback && dateSelectorPlayback, jsx(EGDSSheetContent, {
              padded: false,
              children: jsx("div", {
                className: getClassNameList('uitk-date-selector-dialog-calendar'),
                children: children
              })
            })]
          }) : jsx(EGDSSheetContent, {
            padded: false,
            children: children
          }), !hideFooter && (alertTemplateComponent || !hideDialogSubmit) && jsx(EGDSSpacing, {
            padding: {
              inline: 'three',
              block: 'three'
            },
            children: jsxs(EGDSSheetFooter, {
              children: [alertTemplateComponent && jsx(EGDSAlert, {
                alertTemplateComponent: alertTemplateComponent,
                alertTemplateData: alertTemplateData
              }), !hideDialogSubmit && jsx(EGDSPrimaryButton, {
                "data-stid": "apply-date-selector",
                disabled: submitCta.disabled,
                domRef: skipToButtonRef,
                isFullWidth: true,
                onClick: submitCta.handler,
                size: EGDSButtonSize.LARGE,
                children: submitCta.label
              })]
            })
          })]
        })
      })
    })
  });
};
export { DialogCalendar };